/* eslint-disable max-len */
import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import _ from 'lodash';
import { customSchemas, types as commonTypes } from '@sitebuilder/common';
import * as Grouped from '../../components/GroupedSchema';
import { iconLineHeightSvg, iconTextIndentSvg, iconWordSpacingSvg } from '../../assets/images';
import ThemeEditorContainer from './ThemeEditorContainer';

const ColorSchema: JSONSchema7Definition = {
    type: 'array',
    title: '',
    items: {
        type: 'object',
        properties: {
            key: {
                type: 'string',
                title: 'Enter variable name',
                description: 'Ex: --primary-color',
            },
            value: {
                type: 'string',
                title: 'Enter variable value',
                default: '',
            },
        },
    },
};

const TypographySchema: JSONSchema7Definition = {
    type: 'array',
    title: '',
    items: {
        type: 'object',
        properties: {
            key: {
                type: 'string',
                title: 'Name',
                description: 'Ex: --section-title',
                readOnly: true,
            },
            value: {
                type: 'object',
                title: 'Properties',
                properties: {
                    fontFamily: {
                        type: 'string',
                        title: 'Font Family',
                        default: '',
                    },
                    fontSize: {
                        type: 'string',
                        title: 'Font Size',
                        default: '',
                    },
                    fontWeight: {
                        type: 'string',
                        title: 'Font Weight',
                        enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
                        enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
                        default: 'normal',
                    },
                    letterSpacing: {
                        type: 'string',
                        title: 'Letter Spacing',
                        default: '',
                    },
                    wordSpacing: {
                        type: 'string',
                        title: 'Word Spacing',
                        default: '',
                    },
                    lineHeight: {
                        type: 'string',
                        title: 'Line Height',
                        default: '',
                    },
                },
            },
        },
    },
};

const UserTypographySchema: JSONSchema7Definition = {
    type: 'array',
    title: '',
    items: {
        type: 'object',
        properties: {
            key: {
                type: 'string',
                title: 'Name',
                description: 'Ex: --section-title',
            },
            value: {
                type: 'object',
                title: 'Properties',
                properties: {
                    fontFamily: {
                        type: 'string',
                        title: 'Font Family',
                        default: '',
                    },
                    fontSize: {
                        type: 'string',
                        title: 'Font Size',
                        default: '16px',
                    },
                    fontWeight: {
                        type: 'string',
                        title: 'Font Weight',
                        enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
                        enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
                        default: 'normal',
                    },
                    letterSpacing: {
                        type: 'string',
                        title: 'Letter Spacing',
                        default: '',
                    },
                    wordSpacing: {
                        type: 'string',
                        title: 'Word Spacing',
                        default: '',
                    },
                    lineHeight: {
                        type: 'string',
                        title: 'Line Height',
                        default: '',
                    },
                },
            },
        },
        required: ['key'],
    },
};

const FontSchema: JSONSchema7Definition = {
    type: 'array',
    title: '',
    items: {
        type: 'object',
        properties: {
            name: {
                type: 'string',
                title: 'Font family',
                description: 'Ex: OpenSans-Regular',
            },
            src: {
                type: 'string',
                title: 'Enter font link',
            },
            format: {
                type: 'string',
                title: 'Format',
                enum: _.keys(customSchemas.cssPropertyOptions.FontFormatOptions),
                enumNames: _.values(customSchemas.cssPropertyOptions.FontFormatOptions),
                default: 'truetype',
            },
            fontWeight: {
                type: 'string',
                title: 'Font Weight',
                enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
                enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
                default: 'normal',
            },
            fontStyle: {
                type: 'string',
                title: 'Font Style',
                enum: _.keys(customSchemas.cssPropertyOptions.FontStyleOptions),
                enumNames: _.values(customSchemas.cssPropertyOptions.FontStyleOptions),
                default: 'normal',
            },
            fontDisplay: {
                type: 'string',
                title: 'Font Display',
                enum: _.keys(customSchemas.cssPropertyOptions.FontStyleOptions),
                enumNames: _.values(customSchemas.cssPropertyOptions.FontStyleOptions),
                default: 'normal',
            },
        },
    },
};

const LinkComponentSchema: JSONSchema7Definition = {
    type: 'object',
    title: 'Link',
    properties: {
        fontFamily: {
            type: 'string',
            title: 'Font family',
            description: 'Ex: OpenSans-Regular',
        },
        fontSize: {
            type: 'string',
            title: 'Font Size',
            default: '14px',
        },
        fontWeight: {
            type: 'string',
            title: 'Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        lineHeight: {
            type: 'string',
            title: 'Line Height',
            default: '',
        },
        textColor: {
            type: 'string',
            title: 'Text Color',
            default: '',
        },
        activeTextColor: {
            type: 'string',
            title: 'Active Text Color',
            default: '',
        },
        activeBorderColor: {
            type: 'string',
            title: 'Active Border Color',
            default: '',
        },
        hoverTextColor: {
            type: 'string',
            title: 'Hover Text Color',
            default: '',
        },
        visitedTextColor: {
            type: 'string',
            title: 'Visited Text Color',
            default: '',
        },
        activeBorderWidth: {
            type: 'string',
            title: 'Border',
            default: '',
        },
        activeBorderStyle: {
            type: 'string',
            title: 'Border style',
            enum: _.keys(customSchemas.cssPropertyOptions.BorderStyleOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.BorderStyleOptions),
            default: 'unset',
        },
        textDecoration: {
            type: 'string',
            title: 'Text Decoration',
            default: '',
        },
    },
};

const ButtonComponentSchema: JSONSchema7Definition = {
    type: 'object',
    title: 'Button',
    properties: {
        fontFamily: {
            type: 'string',
            title: 'Font family',
            description: 'Ex: OpenSans-Regular',
        },
        fontSize: {
            type: 'string',
            title: 'Font Size',
            default: '16px',
        },
        fontWeight: {
            type: 'string',
            title: 'Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        lineHeight: {
            type: 'string',
            title: 'Line Height',
            default: '',
        },
        textColor: {
            type: 'string',
            title: 'Text Color',
            default: '',
        },
        primaryBorderColor: {
            type: 'string',
            title: 'Primary Border Color',
            default: '',
        },
        primaryBackgroundColor: {
            type: 'string',
            title: 'Primary Background Color',
            default: '',
        },
        disabledBorderColor: {
            type: 'string',
            title: 'Disabled Border Color',
            default: '',
        },
        disabledBackgroundColor: {
            type: 'string',
            title: 'Disabled Background Color',
            default: '',
        },
        deleteBorderColor: {
            type: 'string',
            title: 'Delete Border Color',
            default: '',
        },
        deleteBackGroundColor: {
            type: 'string',
            title: 'Delete Background Color',
            default: '',
        },
        okBorderColor: {
            type: 'string',
            title: 'Success Border Color',
            default: '',
        },
        okBackGroundColor: {
            type: 'string',
            title: 'Success Background Color',
            default: '',
        },
        borderWidth: {
            type: 'string',
            title: 'Border Width',
            default: '',
        },
        borderStyle: {
            type: 'string',
            title: 'Border style',
            enum: _.keys(customSchemas.cssPropertyOptions.BorderStyleOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.BorderStyleOptions),
            default: 'unset',
        },
        padding: {
            type: 'string',
            title: 'Padding',
            default: '',
        },
        borderRadius: {
            type: 'string',
            title: 'Border Radius',
            default: '',
        },
    },
};

const TableComponentSchema: JSONSchema7 = {
    type: 'object',
    title: 'Table',
    properties: {
        tableWidth: {
            type: 'string',
            title: 'Table Width',
        },
        thFontFamily: {
            type: 'string',
            title: 'Head Font family',
            description: 'Ex: OpenSans-Regular',
        },
        thFontSize: {
            type: 'string',
            title: 'Head Font Size',
            default: '16px',
        },
        thFontWeight: {
            type: 'string',
            title: 'Head Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        thLineHeight: {
            type: 'string',
            title: 'Head Line Height',
            default: '',
        },
        thTextColor: {
            type: 'string',
            title: 'Head Text Color',
            default: '',
        },
        thBorderWidth: {
            type: 'string',
            title: 'Head Border Width',
            default: '',
        },
        thBorderStyle: {
            type: 'string',
            title: 'Head Border style',
            enum: _.keys(customSchemas.cssPropertyOptions.BorderStyleOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.BorderStyleOptions),
            default: 'unset',
        },
        thBorderColor: {
            type: 'string',
            title: 'Head Border Color',
            default: '',
        },
        thPadding: {
            type: 'string',
            title: 'Head Padding',
            default: '',
        },
        tdFontFamily: {
            type: 'string',
            title: 'Body Font family',
            description: 'Ex: OpenSans-Regular',
        },
        tdFontSize: {
            type: 'string',
            title: 'Body Font Size',
            default: '16px',
        },
        tdFontWeight: {
            type: 'string',
            title: 'Body Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        tdLineHeight: {
            type: 'string',
            title: 'Body Line Height',
            default: '',
        },
        tdTextColor: {
            type: 'string',
            title: 'Body Text Color',
            default: '',
        },
        tdBorderWidth: {
            type: 'string',
            title: 'Body Border Width',
            default: '',
        },
        tdBorderStyle: {
            type: 'string',
            title: 'Body Border style',
            enum: _.keys(customSchemas.cssPropertyOptions.BorderStyleOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.BorderStyleOptions),
            default: 'unset',
        },
        tdBorderColor: {
            type: 'string',
            title: 'Body Border Color',
            default: '',
        },
        tdPadding: {
            type: 'string',
            title: 'Body Padding',
            default: '',
        },
        alternatedRowsBGColor: {
            type: 'string',
            title: 'Alternate Rows Background Color',
            default: '',
        },
    },
};

const ListComponentSchema: JSONSchema7 = {
    title: 'List',
    type: 'object',
    properties: {
        listPadding: {
            type: 'string',
            title: 'List Padding',
            default: '',
        },
        listItemPadding: {
            type: 'string',
            title: 'List Item Padding',
            default: '',
        },
        listItemFontFamily: {
            type: 'string',
            title: 'List Item Font family',
            description: 'Ex: OpenSans-Regular',
        },
        listItemFontSize: {
            type: 'string',
            title: 'List Item Font Size',
            default: '16px',
        },
        listItemFontWeight: {
            type: 'string',
            title: 'List Item Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        listItemLineHeight: {
            type: 'string',
            title: 'List Item Line Height',
            default: '',
        },
        listItemTextColor: {
            type: 'string',
            title: 'List Item Text Color',
            default: '',
        },
        listItemBorderWidth: {
            type: 'string',
            title: 'List Item Border Width',
            default: '',
        },
        listItemBorderStyle: {
            type: 'string',
            title: 'List Item Border style',
            enum: _.keys(customSchemas.cssPropertyOptions.BorderStyleOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.BorderStyleOptions),
            default: 'unset',
        },
        listItemBorderColor: {
            type: 'string',
            title: 'List Item Border Color',
            default: '',
        },
        listKeyFontWeight: {
            type: 'string',
            title: 'List Key Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        listKeyFontFamily: {
            type: 'string',
            title: 'List Key Font family',
            description: 'Ex: OpenSans-Regular',
        },
        listKeyFontSize: {
            type: 'string',
            title: 'List Key Font Size',
            default: '16px',
        },
        listKeyTextColor: {
            type: 'string',
            title: 'List Key Text Color',
            default: '',
        },
        listValueFontWeight: {
            type: 'string',
            title: 'List Value Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        listValueFontFamily: {
            type: 'string',
            title: 'List Value Font family',
            description: 'Ex: OpenSans-Regular',
        },
        listValueFontSize: {
            type: 'string',
            title: 'List Value Font Size',
            default: '16px',
        },
        listValueTextColor: {
            type: 'string',
            title: 'List Value Text Color',
            default: '',
        },
        alternateListItemsBgColor: {
            type: 'string',
            title: 'Alternate List Items Background Color',
            default: '',
        },
    },
};

const SectionComponentSchema: JSONSchema7Definition = {
    type: 'object',
    title: 'Section',
    properties: {
        fontFamily: {
            type: 'string',
            title: 'Font family',
            description: 'Ex: OpenSans-Regular',
        },
        fontWeight: {
            type: 'string',
            title: 'Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        fontSize: {
            type: 'string',
            title: 'Font Size',
            default: '16px',
        },
        wordSpacing: {
            type: 'string',
            title: 'Word Spacing',
            default: '',
        },
        lineHeight: {
            type: 'string',
            title: 'Line Height',
            default: '',
        },
        textIndent: {
            type: 'string',
            title: 'Text Indent',
            default: '',
        },
        horizontalAlignment: {
            type: 'string',
            title: 'Horizontal Alignment',
            default: '',
        },
        verticalAlignment: {
            type: 'string',
            title: 'Vertical Alignment',
            default: '',
        },
        padding: {
            type: 'string',
            title: 'Padding',
            default: '',
        },
        margin: {
            type: 'string',
            title: 'Margin',
            default: '',
        },
    },
};

const ModalComponentSchema: JSONSchema7Definition = {
    type: 'object',
    title: 'Modal',
    properties: {
        fontFamily: {
            type: 'string',
            title: 'Font family',
            default: 'OpenSans-Regular',
            description: 'Ex: OpenSans-Regular',
        },
        fontWeight: {
            type: 'string',
            title: 'Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        fontSize: {
            type: 'string',
            title: 'Font Size',
            default: '14px',
        },
        padding: {
            type: 'string',
            title: 'Padding',
            default: '0px 0px 0px 0px',
        },
        margin: {
            type: 'string',
            title: 'Margin',
            default: '1.75rem auto 1.75rem auto',
        },
        maxWidth: {
            type: 'string',
            title: 'Max width',
            default: '800px',
        },
        modalContentDisplay: {
            type: 'string',
            title: 'Modal Content Display',
            enum: ['', 'inline', 'block', 'inline-block', 'flex'],
            default: 'flex',
        },
        modalContentFlexDirection: {
            type: 'string',
            title: 'Modal Content Flex Direction',
            default: 'column',
        },
        modalContentWidth: {
            type: 'string',
            title: 'Modal Content Width',
            default: '100%',
        },
        modalContentColor: {
            type: 'string',
            title: 'Modal Content Color',
            default: 'var(--default-text-color)',
        },
        modalContentBackgroundColor: {
            type: 'string',
            title: 'Modal Content Background Color',
            default: 'var(--color-white)',
        },
        modalContentBorder: {
            type: 'string',
            title: 'Modal Content Border',
            default: '',
        },
        modalContentBorderRadius: {
            type: 'string',
            title: 'Modal Content Border Radius',
            default: '',
        },
        modalHeaderDisplay: {
            type: 'string',
            title: 'Modal Header Display',
            enum: ['', 'inline', 'block', 'inline-block', 'flex'],
            default: 'flex',
        },
        modalHeaderJustifyContent: {
            type: 'string',
            title: 'Modal Header Justify Content',
            default: 'space-between',
        },
        modalHeaderAlignItems: {
            type: 'string',
            title: 'Modal Header Align Items',
            default: 'center',
        },
        modalHeaderBorderBottom: {
            type: 'string',
            title: 'Modal Header Border Bottom',
            default: '',
        },
        modalHeaderBorderTopLeftRadius: {
            type: 'string',
            title: 'Modal Header Border Top-Left Radius',
            default: '',
        },
        modalHeaderBorderTopRightRadius: {
            type: 'string',
            title: 'Modal Header Border Top-Right Radius',
            default: '',
        },
        modalHeaderFontSize: {
            type: 'string',
            title: 'Modal Header Font Size',
            default: '14px',
        },
        modalHeaderFontWeight: {
            type: 'string',
            title: 'Modal Header Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        modalHeaderPadding: {
            type: 'string',
            title: 'Modal Header Padding',
            default: '1rem 1rem 1rem 1rem',
        },
        modalHeaderMargin: {
            type: 'string',
            title: 'Modal Header Margin',
            default: '0px 0px 0px 0px',
        },
        modalBodyFontSize: {
            type: 'string',
            title: 'Modal Body Font Size',
            default: '14px',
        },
        modalBodyFontWeight: {
            type: 'string',
            title: 'Modal Body Font Weight',
            enum: _.keys(customSchemas.cssPropertyOptions.FontWeightOptions),
            enumNames: _.values(customSchemas.cssPropertyOptions.FontWeightOptions),
            default: 'normal',
        },
        modalBodyPadding: {
            type: 'string',
            title: 'Modal Body Padding',
            default: '0px 0px 0px 0px',
        },
        modalBodyMargin: {
            type: 'string',
            title: 'Modal Body Margin',
            default: '1rem 1rem 1rem 1rem',
        },
    },
};

const colorPaletteSchema: JSONSchema7 = {
    title: '',
    type: 'array',
    items: {
        type: 'object',
        required: ['paletteName'],
        properties: {
            paletteName: {
                type: 'string',
                title: 'Palette Name',
                default: '',
                pattern: '^[A-Za-z]+$',
            },
            colors: {
                type: 'array',
                title: 'Add Colors',
                items: {
                    type: 'object',
                    properties: {
                        key: {
                            type: 'string',
                            title: 'Enter variable name',
                            pattern: '^[0-9]+$',
                            description: 'Ex: 10',
                        },
                        value: {
                            type: 'string',
                            title: 'Enter variable value',
                            default: '',
                        },
                    },
                },
            },
        },
    },
};

const schema: JSONSchema7 = {
    title: 'Theme Editor',
    type: 'object',
    required: [],
    properties: {
        defaultColors: ColorSchema,
        colors: ColorSchema,
        typography: TypographySchema,
        userTypography: UserTypographySchema,
        defaultFonts: FontSchema,
        userFonts: FontSchema,
        linkComponent: LinkComponentSchema,
        buttonComponent: ButtonComponentSchema,
        tableComponent: TableComponentSchema,
        listComponent: ListComponentSchema,
        sectionComponent: SectionComponentSchema,
        modalComponent: ModalComponentSchema,
        defaultPalettes: colorPaletteSchema,
        palettes: colorPaletteSchema,
    },
};

// Grouping widget properties
const groups = [
    {
        'Default Colors': ['defaultColors'],
        Colors: ['colors'],
        'Default Typography': ['typography'],
        Typography: ['userTypography'],
        'Default Fonts': ['defaultFonts'],
        Fonts: ['userFonts'],
        Components: [
            'linkComponent',
            'buttonComponent',
            'tableComponent',
            'listComponent',
            'sectionComponent',
            'modalComponent',
        ],
        'Default Color Palettes': ['defaultPalettes'],
        'Color Palettes': ['palettes'],
    },
];

const ThemeEditorSchema: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        'ui:ObjectFieldTemplate': (props: any) => (
            <Grouped.ObjectFieldTemplate {...props} isTheme={true} />
        ),
        defaultColors: {
            items: {
                'ui:field': 'ThemeColorPickerField',
                'ui:options': { isDefaultColor: true, isTextInputDisabled: true },
            },
        },
        colors: {
            items: {
                'ui:field': 'ThemeColorPickerField',
                'ui:options': { isCustomColor: true },
            },
        },
        typography: {
            'ui:options': {
                removable: false,
                addable: false,
            },
            items: {
                key: {
                    'ui:widget': 'CustomTextInput',
                },
                value: {
                    fontFamily: { 'ui:widget': 'FontFamilySelect' },
                    fontSize: {
                        'ui:widget': 'UnitSelectorWidget',
                    },
                    fontWeight: {
                        'ui:widget': 'CustomSelect',
                    },
                    letterSpacing: {
                        'ui:widget': 'UnitSelectorWidget',
                    },
                    wordSpacing: {
                        'ui:widget': 'UnitSelectorWidget',
                    },
                    lineHeight: {
                        'ui:widget': 'UnitSelectorWidget',
                    },
                },
            },
        },
        userTypography: {
            items: {
                key: {
                    'ui:widget': 'CustomTextInput',
                },
                value: {
                    fontFamily: { 'ui:widget': 'FontFamilySelect' },
                    fontWeight: {
                        'ui:widget': 'CustomSelect',
                    },
                    fontSize: {
                        'ui:widget': 'UnitSelectorWidget',
                    },
                    letterSpacing: {
                        'ui:widget': 'UnitSelectorWidget',
                    },
                    wordSpacing: {
                        'ui:widget': 'UnitSelectorWithIcon',
                        'ui:options': { icon: iconWordSpacingSvg },
                    },
                    lineHeight: {
                        'ui:widget': 'UnitSelectorWithIcon',
                        'ui:options': { icon: iconLineHeightSvg },
                    },
                },
            },
        },
        defaultFonts: {
            'ui:options': {
                removable: false,
                addable: false,
            },
            items: {
                name: {
                    'ui:disabled': true,
                    'ui:widget': 'CustomTextInput',
                },
                src: {
                    'ui:widget': 'MediaPicker',
                    'ui:options': {
                        isFontPicker: true,
                    },
                },
                format: {
                    'ui:widget': 'CustomSelect',
                },
                fontStyle: {
                    'ui:widget': 'CustomSelect',
                },
                fontWeight: {
                    'ui:widget': 'CustomSelect',
                },
                fontDisplay: {
                    'ui:widget': 'CustomSelect',
                },
            },
        },
        userFonts: {
            items: {
                name: {
                    'ui:widget': 'CustomTextInput',
                },
                src: {
                    'ui:widget': 'MediaPicker',
                    'ui:options': {
                        isFontPicker: true,
                    },
                },
                format: {
                    'ui:widget': 'CustomSelect',
                },
                fontStyle: {
                    'ui:widget': 'CustomSelect',
                },
                fontWeight: {
                    'ui:widget': 'CustomSelect',
                },
                fontDisplay: {
                    'ui:widget': 'CustomSelect',
                },
            },
        },
        linkComponent: {
            fontFamily: { 'ui:widget': 'FontFamilySelect' },
            fontWeight: {
                'ui:widget': 'CustomSelect',
            },
            fontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            lineHeight: {
                'ui:widget': 'UnitSelectorWidget',
            },
            textColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            activeTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            activeBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            hoverTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            visitedTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            activeBorderWidth: {
                'ui:widget': 'BoxModelProperties',
            },
            activeBorderStyle: {
                'ui:widget': 'CustomSelect',
            },
            textDecoration: { 'ui:widget': 'CustomTextInput' },
        },
        buttonComponent: {
            fontFamily: { 'ui:widget': 'FontFamilySelect' },
            fontWeight: {
                'ui:widget': 'CustomSelect',
            },
            fontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            lineHeight: {
                'ui:widget': 'UnitSelectorWidget',
            },
            textColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            primaryBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            primaryBackgroundColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            disabledBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            disabledBackgroundColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            deleteBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            deleteBackGroundColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            okBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            okBackGroundColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            borderWidth: {
                'ui:widget': 'BoxModelProperties',
            },
            borderStyle: {
                'ui:widget': 'CustomSelect',
            },
            padding: {
                'ui:widget': 'BoxModelProperties',
            },
            borderRadius: {
                'ui:widget': 'UnitSelectorWidget',
            },
        },
        tableComponent: {
            tableWidth: { 'ui:widget': 'Slider' },
            thFontFamily: {
                'ui:widget': 'FontFamilySelect',
            },
            thFontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            thFontWeight: {
                'ui:widget': 'CustomSelect',
            },
            thLineHeight: {
                'ui:widget': 'UnitSelectorWidget',
            },
            thTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            thBorderWidth: {
                'ui:widget': 'BoxModelProperties',
            },
            thBorderStyle: {
                'ui:widget': 'CustomSelect',
            },
            thBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            thPadding: {
                'ui:widget': 'BoxModelProperties',
            },
            tdFontFamily: {
                'ui:widget': 'FontFamilySelect',
            },
            tdFontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            tdFontWeight: {
                'ui:widget': 'CustomSelect',
            },
            tdLineHeight: {
                'ui:widget': 'UnitSelectorWidget',
            },
            tdTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            tdBorderWidth: {
                'ui:widget': 'BoxModelProperties',
            },
            tdBorderStyle: {
                'ui:widget': 'CustomSelect',
            },
            tdBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            tdPadding: {
                'ui:widget': 'BoxModelProperties',
            },
            alternatedRowsBGColor: {
                'ui:widget': 'ThemeColorPicker',
            },
        },
        listComponent: {
            listPadding: {
                'ui:widget': 'BoxModelProperties',
            },
            listItemPadding: {
                'ui:widget': 'BoxModelProperties',
            },
            listItemFontFamily: {
                'ui:widget': 'FontFamilySelect',
            },
            listItemFontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            listItemFontWeight: {
                'ui:widget': 'CustomSelect',
            },
            listItemLineHeight: {
                'ui:widget': 'UnitSelectorWidget',
            },
            listItemTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            listItemBorderWidth: {
                'ui:widget': 'BoxModelProperties',
            },
            listItemBorderStyle: {
                'ui:widget': 'CustomSelect',
            },
            listItemBorderColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            listKeyFontWeight: {
                'ui:widget': 'CustomSelect',
            },
            listKeyFontFamily: {
                'ui:widget': 'FontFamilySelect',
            },
            listKeyFontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            listKeyTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            listValueFontWeight: {
                'ui:widget': 'CustomSelect',
            },
            listValueFontFamily: {
                'ui:widget': 'FontFamilySelect',
            },
            listValueFontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            listValueTextColor: {
                'ui:widget': 'ThemeColorPicker',
            },
            alternateListItemsBgColor: {
                'ui:widget': 'ThemeColorPicker',
            },
        },
        sectionComponent: {
            fontFamily: { 'ui:widget': 'FontFamilySelect' },
            fontWeight: {
                'ui:widget': 'CustomSelect',
            },
            fontSize: {
                'ui:widget': 'UnitSelectorWidget',
            },
            wordSpacing: {
                'ui:widget': 'UnitSelectorWithIcon',
                'ui:options': { icon: iconWordSpacingSvg },
            },
            lineHeight: {
                'ui:widget': 'UnitSelectorWithIcon',
                'ui:options': { icon: iconLineHeightSvg },
            },
            textIndent: {
                'ui:widget': 'UnitSelectorWithIcon',
                'ui:options': { icon: iconTextIndentSvg },
            },
            verticalAlignment: { 'ui:widget': 'VerticalAlignment' },
            horizontalAlignment: { 'ui:widget': 'HorizontalAlignment' },
            padding: { 'ui:widget': 'BoxModelProperties' },
            margin: { 'ui:widget': 'BoxModelProperties' },
        },
        modalComponent: {
            fontFamily: { 'ui:widget': 'FontFamilySelect' },
            fontWeight: { 'ui:widget': 'CustomSelect' },
            fontSize: { 'ui:widget': 'UnitSelectorWidget' },
            padding: { 'ui:widget': 'BoxModelProperties' },
            margin: { 'ui:widget': 'BoxModelProperties' },
            maxWidth: { 'ui:widget': 'CustomTextInput' },
            modalContentDisplay: { 'ui:widget': 'DisplayOption' },
            modalContentFlexDirection: { 'ui:widget': 'FlexDirectionProperty' },
            modalContentWidth: { 'ui:widget': 'CustomTextInput' },
            modalContentColor: { 'ui:widget': 'ThemeColorPicker' },
            modalContentBackgroundColor: { 'ui:widget': 'ThemeColorPicker' },
            modalContentBorder: { 'ui:widget': 'CustomTextInput' },
            modalContentBorderRadius: { 'ui:widget': 'CustomTextInput' },
            modalHeaderDisplay: { 'ui:widget': 'DisplayOption' },
            modalHeaderJustifyContent: { 'ui:widget': 'ItemHorizontalAlign' },
            modalHeaderAlignItems: { 'ui:widget': 'ItemVerticalAlign' },
            modalHeaderBorderBottom: { 'ui:widget': 'CustomTextInput' },
            modalHeaderBorderTopLeftRadius: { 'ui:widget': 'CustomTextInput' },
            modalHeaderBorderTopRightRadius: { 'ui:widget': 'CustomTextInput' },
            modalHeaderFontSize: { 'ui:widget': 'UnitSelectorWidget' },
            modalHeaderFontWeight: { 'ui:widget': 'CustomSelect' },
            modalHeaderPadding: { 'ui:widget': 'BoxModelProperties' },
            modalHeaderMargin: { 'ui:widget': 'BoxModelProperties' },
            modalBodyFontSize: { 'ui:widget': 'UnitSelectorWidget' },
            modalBodyFontWeight: { 'ui:widget': 'CustomSelect' },
            modalBodyPadding: { 'ui:widget': 'BoxModelProperties' },
            modalBodyMargin: { 'ui:widget': 'BoxModelProperties' },
        },
        defaultPalettes: {
            'ui:options': {
                removable: false,
                addable: false,
            },
            items: {
                paletteName: { 'ui:widget': 'CustomTextInput', 'ui:disabled': true },
                colors: {
                    'ui:options': {
                        removable: false,
                        addable: false,
                    },
                    items: {
                        value: { 'ui:widget': 'ThemeColorPicker' },
                        key: {
                            'ui:disabled': true,
                            'ui:widget': 'CustomTextInput',
                        },
                    },
                },
            },
        },
        palettes: {
            items: {
                paletteName: { 'ui:widget': 'CustomTextInput' },
                colors: {
                    items: {
                        value: { 'ui:widget': 'ThemeColorPicker' },
                        key: {
                            'ui:widget': 'CustomTextInput',
                        },
                    },
                },
            },
        },
    },
    templateView: ThemeEditorContainer,
    id: 'themeEditor',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default ThemeEditorSchema;
